/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Ronnel Bedana - ronnel@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { SearchableItem, SearchableSelectionType } from './interface';
import { ReportType } from './report';

export class Quote {
  ID: string;
  SERVICE: ServiceType;
  BV_TYPE: ReportType;
  EA_TYPE: AppraisalType;
  PURPOSE_FINANCING: boolean;
  FINANCING_PURPOSE_TYPE: FinancingPurposeType;
  PURPOSE_DESC: string;
  EA_LOCATIONS: string;
  EA_ENG_ADDRESS: string;
  RUSH: boolean;
  RUSH_SPEED: number;
  BANK_NAME: string;
  PERSON_SUBMIT: Contact;
  PERSON_LENDER: Contact;
  COMPANY_NAME: string;
  COMPANY_INDUSTRY: string;
  REVENUE: Revenue[];
  OPERATES_FROM: Operates;
  OPERATES_UNDER: Operates;

  REPORT_PURPOSE: ReportPurpose;
  REPORT_PURPOSE_EXPLAIN: string;
  BANK_FINANCE_PURPOSE: BankFinancePurpose;
  BANK_FINANCE_PURPOSE_EXPLAIN: string;
  PURCHASE_STRUCTURE: PurchaseStructure;
  IS_REAL_ESTATE_INCLUDED: boolean;

  COMMENTS: string;
  EASY_USE: boolean;
  SUGGESTIONS: string;
  EQ_LIST: boolean;
  EQ_LIST_FILE: Attachment[];
  
  FIRST_QUOTE_FOLLOW_UP: number;
  SECOND_QUOTE_FOLLOW_UP: number;

  constructor(value: any = {}) {
    this.REVENUE = [];
    this.PERSON_SUBMIT = new Contact();
    this.PERSON_LENDER = new Contact();
    this.COMMENTS = '';
    this.OPERATES_FROM = new Operates();
    this.OPERATES_UNDER = new Operates();

    Object.assign(this, value);

    if(value.REVENUE)
      this.REVENUE = Revenue.parseArray(value.REVENUE);

    if(value.PERSON_SUBMIT)
      this.PERSON_SUBMIT = new Contact(value.PERSON_SUBMIT);

    if(value.PERSON_LENDER)
      this.PERSON_LENDER = new Contact(value.PERSON_LENDER);

    if(value.OPERATES_FROM)
      this.OPERATES_FROM = new Operates(value.OPERATES_FROM);

    if(value.OPERATES_UNDER)
      this.OPERATES_UNDER = new Operates(value.OPERATES_UNDER);

    if(value.EQ_LIST_FILE)
      this.EQ_LIST_FILE = Attachment.parseArray(value.EQ_LIST_FILE);
  }

  static parseArray(json: any[]): Quote[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(new Quote(j));

    return arr;
  }
}

export class Attachment {
  ID: string;
  NAME: string;
  DATA: string;

  constructor(value: Object = {}) {
    Object.assign(this, value);
  }

  static parseArray(json: any[]): Attachment[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(new Attachment(j));

    return arr;
  }
}

export class Contact implements SearchableItem {
  NAME: string;
  CONTACT: string;
  PHONE: string;
  EMAIL: string;
  OTHER: string;
  PHONE_EXT: string;

  // Helpers
  active = false;
  allowed = false;

  constructor(value: Object = {}) {
    Object.assign(this, value);
  }

  getName(): string {
    return `${this.NAME ? this.NAME : this.EMAIL}`;
  }

  getFullDetails(): string {
    return `${this.NAME ? this.NAME : ''} ${this.EMAIL ? '<' + this.EMAIL + '>' : ''}`;
  }

  getType(): SearchableSelectionType {
    return SearchableSelectionType.VALUE;
  }

  getValue(): any {
    return this;
  }
}

export class Operates {
  ID: string;
  IS_MULTIPLE: boolean;
  AMOUNT: string;

  constructor(value: any = {}) {
    Object.assign(this, value);
  }
}

export class Revenue {
  ID: string;
  YEAR: number;
  VALUE: number;

  constructor(value: any = {}) {
    Object.assign(this, value);
  }

  static parseArray(json: any[]): Revenue[] {
    const arr = [];

    if (json) {
      for (const j of json) {
        if (j) {
          arr.push(new Revenue(j));
        }
      }
    }

    return arr;
  }
}

export enum AppraisalType {
  DESKTOP = 'DESKTOP',
  SUMMARY = 'SUMMARY',
  BOTH = 'BOTH'
}

export enum ValuationType {
  CONCLUSION = 'CONCLUSION',
  CALCULATION = 'CALCULATION',
  IV = 'IV',
  OTHER = ''
}

export enum ServiceType {
  BV = 'BV',
  EA = 'EA',
  BOTH = 'BOTH'
}

export enum ReportPurpose {
  BANK_FINANCING = 'BANK_FINANCING',
  FUTURE_SALE = 'FUTURE_SALE',
  OTHER = 'OTHER'
}

export enum BankFinancePurpose {
  PURCHASE = 'PURCHASE',
  DEBT_REF = 'DEBT_REF',
  COLLATERAL = 'COLLATERAL',
  OTHER = 'OTHER'
}

export enum PurchaseStructure {
  ASSET_SALE = 'ASSET_SALE',
  STOCK_SALE = 'STOCK_SALE',
  DETERMINED = 'DETERMINED'
}


export enum FinancingPurposeType {
  SBA_LOAN = 'SBA_LOAN',
  CONVENTIONAL_LOAN = 'CONVENTIONAL_LOAN'
}


export enum ClientType {
  LENDER = 'LENDER',
  OWNER_BUYER = 'OWNER_BUYER',
  OTHER = 'OTHER'
}
export enum QuoteType {
  LOST = 'LOST',
   WON= 'WON',
}